import { App } from 'antd';
import { useCallback, useEffect, useRef } from 'react';

import { connectionsActions } from '@store/connections/connections.slice';
import { integrationsActions } from '@store/integrations/integrations.slice';

import { useCurrentApplication } from '@hook/application/use-current-application.hook';
import { useLazyQuery } from '@hook/common/use-lazy-query.hook';
import { useMutation } from '@hook/common/use-mutation.hook';
import { useSaveConnectionMutation } from '@hook/connection/connection.hooks';
import { useLazyLoadIntegrationsQuery } from '@hook/intergration/integration.hooks';
import { ConnectorInterface } from '@interface/connector/connector.interface';
import { isExist } from '@util/data/is-exist';

export const useIntegrationModal = (connector: ConnectorInterface, onClose: () => void) => {
  const { message } = App.useApp();
  const needClose = useRef(true);
  const { current } = useCurrentApplication();

  const { action: saveConnection, state } = useMutation(
    useSaveConnectionMutation(),
    connectionsActions,
    connectionsActions.setConnection,
  );

  const { action } = useLazyQuery(
    useLazyLoadIntegrationsQuery({ refetchOnReconnect: true }),
    integrationsActions,
    integrationsActions.setData,
  );

  useEffect(() => {
    if (state.isSuccess) {
      message.success('Integration was successfully updated');

      if (needClose.current) onClose();
    }
  }, [state.isSuccess]);

  return useCallback(
    (data: any, shouldClose = true) => {
      needClose.current = shouldClose;

      if (connector.serviceId === 'velory' && isExist(data.settings?.domain)) {
        data.settings.domain = data.settings.domain.replace(/^https?:\/\//, '').replace(/\.?velory.com/, '');
      }

      void saveConnection({
        unifiedApi: connector.unifiedApi,
        serviceId: connector.serviceId,
        ...data,
      }).then(() => action(current.id, false));
    },
    [connector, saveConnection, current.id],
  );
};
